import { useMemo, useEffect, useReducer, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAdobeAuthZ } from 'actions/auth';
import intersection from 'lodash/intersection';
import { useNetworkResId } from 'hooks/kitchenSink';
import { VideoCtvRedux } from '../../store-types';
import { Provider, Providers, Video } from 'app-types';

interface AuthZState {
  loading: boolean;
  validatedVideoId: string | null;
  error: boolean;
}

type AuthZAction =
  | { type: 'GET_AUTHZ_STARTED' }
  | { type: 'GET_AUTHZ_COMPLETED'; payload: string }
  | { type: 'AUTHZ_ERROR' }
  | { type: 'RESET_AUTHZ_STATE' };

// This checks for authentication.  In the context of
// paramount plus, this does not check if the user is currently
// subscribed, it only validates that they have an account
export const useCurrentSubscriptions = (): Provider[] => {
  const isAdobeSubscribed = useSelector((state: VideoCtvRedux) => {
    try {
      return !!state.auth.adobe.authN;
    } catch {
      return false;
    }
  });
  const isParamountPlusSubscribed = useSelector((state: VideoCtvRedux) => {
    try {
      return !!state.auth.paramountPlus.user.data?.userId;
    } catch {
      return false;
    }
  });

  const currentSubscriptions = useMemo(() => {
    const result: Provider[] = [];
    if (isAdobeSubscribed) result.push(Providers.ADOBE);
    if (isParamountPlusSubscribed) result.push(Providers.PARAMOUNT_PLUS);

    return result;
  }, [isAdobeSubscribed, isParamountPlusSubscribed]);

  return currentSubscriptions;
};

const useParamountPlusAuthZ = (): boolean => {
  const userInfo = useSelector(
    (state: VideoCtvRedux) => state.auth.paramountPlus.user.data,
  );
  return !!userInfo?.subscriber;
};

const useAdobeAuthZ = (
  video: Video | null | undefined,
  shouldAuthorize: boolean,
): [boolean, boolean, boolean] => {
  const network = video?.network;
  const videoId = video?.id;

  const initialState: AuthZState = {
    loading: false,
    validatedVideoId: null,
    error: false,
  };

  const reducer = (prevState: AuthZState, action: AuthZAction): AuthZState => {
    switch (action.type) {
      case 'GET_AUTHZ_STARTED':
        return {
          loading: true,
          validatedVideoId: null,
          error: false,
        };
      case 'GET_AUTHZ_COMPLETED':
        return {
          loading: false,
          validatedVideoId: action.payload,
          error: false,
        };
      case 'AUTHZ_ERROR':
        return {
          error: true,
          validatedVideoId: null,
          loading: false,
        };
      case 'RESET_AUTHZ_STATE':
        return initialState;
      default:
        return prevState;
    }
  };

  const [state, stateDispatch] = useReducer(reducer, initialState);
  const videoIdRef = useRef<string | null>(null);

  const resource = useNetworkResId(network);

  useEffect(() => {
    if (videoId) {
      videoIdRef.current = videoId;
    }

    return () => {
      stateDispatch({ type: 'RESET_AUTHZ_STATE' });
    };
  }, [videoId]);

  const dispatch = useDispatch();
  useEffect(() => {
    let isUnmounted = false;
    const unmountHandler = () => {
      isUnmounted = true;
    };
    if (shouldAuthorize && resource) {
      stateDispatch({ type: 'GET_AUTHZ_STARTED' });
      const fetchAuthorization = async () => {
        const response = await dispatch(fetchAdobeAuthZ(resource) as any);
        if (isUnmounted) {
          return;
        }
        // Handles cases of multiple/stale auth z dispatches
        if (videoIdRef.current === videoId) {
          if (response) {
            stateDispatch({
              type: 'GET_AUTHZ_COMPLETED',
              payload: videoId as string,
            });
          } else {
            stateDispatch({ type: 'AUTHZ_ERROR' });
          }
        }
      };

      fetchAuthorization();
    }
    return unmountHandler;
  }, [dispatch, resource, shouldAuthorize, videoId]);

  // Consider success via videoId this way to ensure we properly "re-initialize" the authN hook for new videos
  return [state.validatedVideoId === videoId, state.loading, state.error];
};

export const useAuthZ = (
  video: Video | null | undefined,
): [boolean, Provider | null, boolean, boolean] => {
  let isAuthorized = false;
  let authorizedProvider: Provider | null = null;
  const authTypes = video?.authTypes || [];

  if (authTypes.length === 0) {
    isAuthorized = true;
  }

  const isPPlusAuthorized = useParamountPlusAuthZ();
  if (!isAuthorized && authTypes.includes(Providers.PARAMOUNT_PLUS)) {
    isAuthorized = isPPlusAuthorized;
    if (isPPlusAuthorized) authorizedProvider = Providers.PARAMOUNT_PLUS;
  }

  const [isAdobeAuthorized, loading, error] = useAdobeAuthZ(
    video,
    !isAuthorized && authTypes.includes(Providers.ADOBE),
  );

  if (!isAuthorized && authTypes.includes(Providers.ADOBE)) {
    isAuthorized = isAdobeAuthorized;
    if (isAdobeAuthorized) authorizedProvider = Providers.ADOBE;
  }
  return [isAuthorized, authorizedProvider, loading, error];
};

// Not using video since settings tiles can use this hook to determine whether
// they should show sign in or sign out
export const useAuthN = (authTypes: Provider[]): boolean => {
  const getAuthNStatus = useGetAuthNStatus();

  return getAuthNStatus(authTypes);
};

export const useGetAuthNStatus = (): ((authTypes: Provider[]) => boolean) => {
  const currentSubscriptions = useCurrentSubscriptions();

  return (authTypes: Provider[]) => {
    return (
      authTypes.length === 0 ||
      intersection(authTypes, currentSubscriptions).length > 0
    );
  };
};
